import { PRISM_FPS_WINDOW_MS } from './prismConstants';
import type { PrismFpsSample } from './prismTypes';

interface PrismFrameMetricsRecordInput {
  timestamp: number;
  renderMs: number;
  fpsSampler: (sample: PrismFpsSample) => void;
}

export class PrismFrameMetrics {
  private prismPrevFrameTimestamp = 0;
  private fpsWindowStart = 0;
  private fpsWindowFrames = 0;
  private fpsRenderMsSum = 0;
  private fpsRenderMsMax = 0;
  private fpsDeltaMsSum = 0;
  private fpsDeltaMsMax = 0;
  private fpsDeltaCount = 0;

  record({ timestamp, renderMs, fpsSampler }: PrismFrameMetricsRecordInput): void {
    const frameDeltaMs = this.prismPrevFrameTimestamp === 0 ? 0 : timestamp - this.prismPrevFrameTimestamp;
    this.prismPrevFrameTimestamp = timestamp;

    this.recordFpsSample({ timestamp, renderMs, frameDeltaMs, fpsSampler });
  }

  private recordFpsSample({
    timestamp,
    renderMs,
    frameDeltaMs,
    fpsSampler,
  }: {
    timestamp: number;
    renderMs: number;
    frameDeltaMs: number;
    fpsSampler: (sample: PrismFpsSample) => void;
  }): void {
    this.ensureFpsWindow(timestamp);
    this.recordFpsRenderMs(renderMs);
    this.recordFpsFrameDelta(frameDeltaMs);
    this.emitFpsWindow({ timestamp, fpsSampler });
  }

  private ensureFpsWindow(timestamp: number): void {
    if (this.fpsWindowStart === 0) this.fpsWindowStart = timestamp;
  }

  private recordFpsRenderMs(renderMs: number): void {
    this.fpsWindowFrames += 1;
    this.fpsRenderMsSum += renderMs;
    if (renderMs > this.fpsRenderMsMax) this.fpsRenderMsMax = renderMs;
  }

  private recordFpsFrameDelta(frameDeltaMs: number): void {
    if (frameDeltaMs <= 0) return;

    this.fpsDeltaMsSum += frameDeltaMs;
    this.fpsDeltaCount += 1;
    if (frameDeltaMs > this.fpsDeltaMsMax) this.fpsDeltaMsMax = frameDeltaMs;
  }

  private emitFpsWindow({
    timestamp,
    fpsSampler,
  }: {
    timestamp: number;
    fpsSampler: (sample: PrismFpsSample) => void;
  }): void {
    const windowMs = timestamp - this.fpsWindowStart;
    if (windowMs < PRISM_FPS_WINDOW_MS) return;

    fpsSampler(this.createFpsSample(windowMs));
    this.resetFpsWindow(timestamp);
  }

  private createFpsSample(windowMs: number): PrismFpsSample {
    return {
      windowMs,
      frames: this.fpsWindowFrames,
      fps: (this.fpsWindowFrames * 1000) / windowMs,
      avgRenderMs: average(this.fpsRenderMsSum, this.fpsWindowFrames),
      maxRenderMs: this.fpsRenderMsMax,
      avgFrameDeltaMs: average(this.fpsDeltaMsSum, this.fpsDeltaCount),
      maxFrameDeltaMs: this.fpsDeltaMsMax,
    };
  }

  private resetFpsWindow(timestamp: number): void {
    this.fpsWindowStart = timestamp;
    this.fpsWindowFrames = 0;
    this.fpsRenderMsSum = 0;
    this.fpsRenderMsMax = 0;
    this.fpsDeltaMsSum = 0;
    this.fpsDeltaMsMax = 0;
    this.fpsDeltaCount = 0;
  }
}

function average(total: number, count: number): number {
  if (count <= 0) return 0;
  return total / count;
}
